import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useNavigateWithOrigin } from '../../app/nav/originNav'
import Button from '../../components/ui/Button'
import PageHeader from '../../components/ui/PageHeader'
import RefreshButton from '../../components/ui/RefreshButton'
import ActivityFilters, {
  type ActivityCounts,
  type ActivityFilterValue,
} from './components/ActivityFilters'
import ActivityKpis from './components/ActivityKpis'
import ActivityTable from './components/ActivityTable'
import { useActivities } from './hooks/useActivities'
import { useActivityLookups } from './hooks/useActivityLookups'
import { useActivitySummary } from './hooks/useActivitySummary'
import { useUpdateParticipation } from './hooks/useUpdateParticipation'
import { useToast } from '../../components/ui/ToastProvider'
import { searchTerm } from '../../lib/searchTerm'
import { appStorageKey } from '../../lib/appStorage'
import { exportActivities } from './api/activityApi'
import { getApiErrorMessage } from '../../api/apiError'
import { useGrants } from '../../auth/useCurrentUser'
import { GRANTS } from '../../auth/grants'
import type { PriorityLevel } from './types'

/** Sabit sayfa boyutu varsayılanı (anayasa: liste varsayılan 10). */
const DEFAULT_PAGE_SIZE = 10

const EMPTY_FILTERS: ActivityFilterValue = {
  statusId: null,
  priority: null,
  eventTypeId: null,
  invitationTypeId: null,
  countryId: null,
  q: '',
}

// Görünüm durumu kalıcılığı (016 kalıbı): oturum-içi depolama (sessionStorage). Serileştirme için hafif
// bir sorgu-dizesi biçimi kullanılır (yalnız depolamada; URL'e yazılmaz). Anahtar uygulama ön eklidir
// (062 research D7a) — kullanıcı/persona geçişinde ön ek taramasıyla otomatik temizlenir.
const VIEW_STORAGE_KEY = appStorageKey('activity-list-view')

/** Sorgu parametresini sayı-veya-null'a çevirir (boş/geçersiz → null). */
function numOrNull(value: string | null): number | null {
  if (value == null || value === '') return null
  const n = Number(value)
  return Number.isFinite(n) ? n : null
}

/** Geçerli öncelik değeri veya null (bozuk kayıt → null). */
function priorityOrNull(value: string | null): PriorityLevel | null {
  return value === 'LOW' || value === 'MEDIUM' || value === 'HIGH' ? value : null
}

/** Kayıtlı sorgu-dizesini görünüm durumuna (filtre + sayfa + boyut) çevirir. */
function parseView(sp: URLSearchParams): {
  filters: ActivityFilterValue
  page: number
  pageSize: number
} {
  return {
    filters: {
      statusId: numOrNull(sp.get('status')),
      priority: priorityOrNull(sp.get('priority')),
      eventTypeId: numOrNull(sp.get('et')),
      invitationTypeId: numOrNull(sp.get('it')),
      countryId: numOrNull(sp.get('co')),
      // Arama metni kalıcı DEĞİL (kullanıcı kuralı 2026-08-31): sayfadan çıkıp dönünce sıfırlanır.
      // Bu görünüm yalnız sessionStorage'dan okunur (URL deep-link'i yok) → boşlamak yeterli;
      // yazma tarafı da `q`'yu atlar, aksi halde eski oturum kaydı bir kez geri yüklenirdi.
      q: '',
    },
    page: Math.max(0, numOrNull(sp.get('page')) ?? 0),
    // Bozuk/uyumsuz kayıtta (ör. size=0) geçerli pozitif değilse varsayılana düş (boş sayfa isteği önlenir).
    pageSize: ((s) => (s != null && s > 0 ? s : DEFAULT_PAGE_SIZE))(numOrNull(sp.get('size'))),
  }
}

/** Başlangıç görünümü: sessionStorage'daki son görünüm (F5 / menüden dönüş); yoksa boş (ilk giriş). */
function initialView() {
  let stored: string
  try {
    stored = sessionStorage.getItem(VIEW_STORAGE_KEY) ?? ''
  } catch {
    stored = ''
  }
  return parseView(new URLSearchParams(stored))
}

/**
 * Etkinlik Listesi (US1 + US3). Talep Listesi kalıbı: PageHeader + filtre/arama + sunucu-taraflı sayfalanan
 * tablo. Görünüm durumu (filtre/arama/sayfa/boyut) sessionStorage'da tutulur (F5 / menüden dönüşte korunur).
 * KPI kartları (US4) sonraki fazda eklenir. Satıra tıklama detay/özete gider (ayrı feature; placeholder).
 */
export default function ActivityListPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  // Yönetici Özeti'ne giderken bu listenin konumu (path + URL query'si) origin taşınır → özet ekranında "Geri"
  // buraya döner. Filtre/görünüm kalıcılığını origin DEĞİL, aşağıdaki sessionStorage (016) sağlar — ayrı mekanizma.
  const goDetail = useNavigateWithOrigin()
  const toast = useToast()
  const participation = useUpdateParticipation()
  // Excel butonu yalnız ACTIVITY_EXPORT yetkisi olan kullanıcıya görünür (FR-016; asıl koruma backend'de).
  const { hasGrant } = useGrants()

  // İşlemler menüsü → katılım durumu (GM kararı) güncelle; başarıda liste/KPI tazelenir (hook).
  const onParticipation = (id: number, statusId: number) =>
    participation.mutate(
      { id, statusId },
      {
        onSuccess: () =>
          toast.show({ severity: 'success', summary: t('activity.actions.updated'), life: 3000 }),
        onError: () =>
          toast.show({ severity: 'error', summary: t('common.errors.generic'), life: 3000 }),
      },
    )

  // Başlangıç görünümü mount'ta bir kez hesaplanır (sessionStorage'daki son görünüm, yoksa boş).
  const initialRef = useRef<ReturnType<typeof initialView> | null>(null)
  if (initialRef.current === null) initialRef.current = initialView()
  const initial = initialRef.current

  const [filters, setFilters] = useState<ActivityFilterValue>(initial.filters)
  const [page, setPage] = useState(initial.page)
  const [pageSize, setPageSize] = useState(initial.pageSize)
  const [debouncedQ, setDebouncedQ] = useState(initial.filters.q)
  const [exporting, setExporting] = useState(false)

  // Arama kutusu her tuşta istek atmasın: q'yu 300ms debounce et (filtreler anında uygulanır).
  useEffect(() => {
    const id = setTimeout(() => setDebouncedQ(filters.q), 300)
    return () => clearTimeout(id)
  }, [filters.q])

  // State → sessionStorage senkronu: yalnız varsayılandan sapan alanlar hafif bir sorgu-dizesi olarak
  // kaydedilir; sonraki girişte (F5 / menüden dönüş) geri yüklenir.
  useEffect(() => {
    const p = new URLSearchParams()
    if (filters.statusId != null) p.set('status', String(filters.statusId))
    if (filters.priority != null) p.set('priority', filters.priority)
    if (filters.eventTypeId != null) p.set('et', String(filters.eventTypeId))
    if (filters.invitationTypeId != null) p.set('it', String(filters.invitationTypeId))
    if (filters.countryId != null) p.set('co', String(filters.countryId))
    // `q` BİLEREK yazılmaz — arama metni kalıcı değildir (bkz. parseView).
    if (page > 0) p.set('page', String(page))
    if (pageSize !== DEFAULT_PAGE_SIZE) p.set('size', String(pageSize))
    try {
      sessionStorage.setItem(VIEW_STORAGE_KEY, p.toString())
    } catch {
      /* sessionStorage kullanılamıyorsa kalıcılık atlanır */
    }
  }, [filters, page, pageSize])

  const query = useMemo(
    () => ({
      statusId: filters.statusId,
      priority: filters.priority,
      eventTypeId: filters.eventTypeId,
      invitationTypeId: filters.invitationTypeId,
      countryId: filters.countryId,
      q: searchTerm(debouncedQ), // ≥2 karakterde arama; altında tüm liste (021)
      page,
      size: pageSize,
    }),
    [
      filters.statusId,
      filters.priority,
      filters.eventTypeId,
      filters.invitationTypeId,
      filters.countryId,
      debouncedQ,
      page,
      pageSize,
    ],
  )

  const list = useActivities(query)
  const lookups = useActivityLookups()
  const summary = useActivitySummary()
  const items = list.data?.content ?? []
  const totalElements = list.data?.page.totalElements ?? 0

  // Durum sekmesi sayı rozetleri (kapsam-geneli; KPI ile aynı kaynak — filtreden bağımsız).
  const counts: ActivityCounts | undefined = summary.data
    ? {
        all: summary.data.total,
        byStatus: {
          350: summary.data.waiting,
          351: summary.data.attending,
          352: summary.data.notAttending,
        },
      }
    : undefined

  // Filtre değişince ilk sayfaya dön (kullanıcı sonuçların başında kalır).
  const patchFilters = (patch: Partial<ActivityFilterValue>) => {
    setFilters((prev) => ({ ...prev, ...patch }))
    setPage(0)
  }

  // Tüm filtre + aramayı tek adımda sıfırla. debouncedQ'yu da hemen boşalt ki 300 ms gecikme olmadan
  // liste filtresiz yüklensin.
  const clearFilters = () => {
    setFilters(EMPTY_FILTERS)
    setDebouncedQ('')
    setPage(0)
  }

  // Excel dışa aktarma: aktif filtrelerle sunucudan .xlsx çeker (tüm filtreli kayıtlar) ve indirir.
  // Buton yalnız ACTIVITY_EXPORT grant'i olan kullanıcıya görünür (hasGrant kontrolü aşağıda); asıl koruma
  // backend'de (@PreAuthorize ACTIVITY_EXPORT + ACTIVITY_VIEW).
  const onExport = async () => {
    setExporting(true)
    try {
      await exportActivities(query)
    } catch (err) {
      // Varsa backend'in lokalize mesajı; yoksa genel hata.
      toast.show({
        severity: 'error',
        summary: getApiErrorMessage(err) ?? t('common.errors.generic'),
        life: 3000,
      })
    } finally {
      setExporting(false)
    }
  }

  return (
    <div className="ui-page">
      <PageHeader
        title={t('activity.list.title')}
        actions={
          <>
            {/* Yenile, dışa aktarmanın SOLUNDA (kullanıcı isteği 2026-08-27); önceden filtre
                çubuğundaydı (`ActivityFilters` → `FilterBar onRefresh`). */}
            <RefreshButton onRefresh={() => list.refetch()} />
            {/* Görünüm yanındaki "Yenile" ile AYNI (kullanıcı kararı 2026-08-27):
                paylaşılan `.ui-toolbar-btn`. Bu sayfa paylaşılan `ExcelExportButton`'ı
                kullanmadığı için o bileşendeki `outlined`→`text` değişikliğini almamıştı
                ve iki komşu buton farklı görünüyordu. Dışa aktarma MANTIĞI taşınmadı
                (kendi `onExport`/`exporting` durumu yerinde) — yalnız görünüm hizalandı. */}
            {hasGrant(GRANTS.ACTIVITY_EXPORT) && (
              <Button
                label={t('activity.list.export')}
                icon="pi pi-file-excel"
                text
                className="ui-toolbar-btn"
                loading={exporting}
                onClick={onExport}
              />
            )}
            {/* Kapı EKLENDİ (push kapısı review'ü 21.10.2026): düğme hiçbir yetki sormuyordu, oysa
                gittiği rota `ACTIVITY_CREATE`'e kilitli ({@code menu.ts}) — o yetkisi olmayan
                kullanıcı düğmeyi görüp tıklıyor ve "yetkiniz yok" boş durumuna düşüyordu. Kısıtlı
                Yetkili asistanda da aynısı oluyordu (FR-019: oluşturma eylemi SUNULMAMALIDIR,
                sunulup hata vermemelidir). Ölçüt `readOnly` DEĞİL yetkidir: persona kipi o kodu
                zaten düşürüyor, yetki ölçütü ise genel açığı da kapatıyor. */}
            {hasGrant(GRANTS.ACTIVITY_CREATE) && (
              <Button
                label={t('activity.list.new')}
                icon="pi pi-plus"
                onClick={() => navigate('/events/new')}
              />
            )}
          </>
        }
      />
      <ActivityKpis summary={summary.data} loading={summary.isLoading} />
      {/* Bölüm başlığı panelin DIŞINDA (kullanıcı isteği 2026-08-27): filtre satırı artık tabloya
          yapışık tek beyaz kart çiziyor; başlık panelin içinde kaldığında o kartla aynı gruba
          ait görünüyordu. Yukarı alınınca kartın üstünde bağımsız bir bölüm başlığı olur. */}
      <div className="ui-panel-head">
        <h2 className="ui-panel-title">{t('activity.list.panelTitle')}</h2>
      </div>
      <div className="ui-panel">
        <ActivityFilters
          lookups={lookups.data}
          counts={counts}
          value={filters}
          onChange={patchFilters}
          onClear={clearFilters}
        />
        <ActivityTable
          items={items}
          loading={list.isLoading}
          page={page}
          rows={pageSize}
          totalElements={totalElements}
          onPage={(nextPage, nextRows) => {
            setPage(nextPage)
            setPageSize(nextRows)
          }}
          onReview={(id) => goDetail('/events/executive-summary?id=' + id)}
          onParticipation={onParticipation}
        />
      </div>
    </div>
  )
}
